import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Autocomplete,
  Typography,
  Box,
  FormHelperText,
  Alert,
} from '@mui/material';
import { meses } from '../../../../helpers/months.ts';
import { getForecastById } from '../../../../services/forecastRevenueManager/forecastRevenueManager.ts';
import { ForecastData, UnitOption } from '../types.ts';
import { formatarValor } from '../../../../helpers/formatarValor.ts';
import { useUnidades } from '../../../../contexts/useUnidades.js';

interface ForecastModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (forecastData: ForecastData) => void;
  existingForecastId?: string;
  cookies: any;
}

const ForecastModal: React.FC<ForecastModalProps> = ({ open, onClose, onSave, existingForecastId, cookies }) => {
  const unidades: { unidade: string; unidade_id: string; uhs?: number; hasUhs: boolean }[] = useUnidades();

  const [unitOptions, setUnitOptions] = useState<UnitOption[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<UnitOption | null>(null);
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [aptsOcupados, setAptsOcupados] = useState<number>(0);
  const [diariaMedia, setDiariaMedia] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [taxaOcupacao, setTaxaOcupacao] = useState<number>(0);
  const [revPar, setRevPar] = useState<number>(0);
  const [receitaHospedagem, setReceitaHospedagem] = useState<number>(0);
  const [disponivel, setDisponivel] = useState<number>(0);
  const [disponivelReadOnly, setDisponivelReadOnly] = useState<boolean>(true);

  useEffect(() => {
    if (unidades.length > 0) {
      const diasNoMes = new Date(new Date().getFullYear(), month, 0).getDate();

      const options = unidades
        .filter((unit) => unit.uhs !== null)
        .map((unit) => ({
          label: unit.unidade,
          value: unit.unidade_id,
          disponivel: unit.uhs ? unit.uhs * diasNoMes : 0,
          hasUhs: !!unit.uhs,
        }));

      setUnitOptions(options);
    }
  }, [unidades, month]);

  useEffect(() => {
    const loadExistingForecast = async () => {
      if (existingForecastId) {
        try {
          setIsEditing(true);
          const forecast = await getForecastById(cookies, existingForecastId);

          const unit = unitOptions.find((option) => option.value === forecast.unidade_id);
          if (unit) {
            setSelectedUnit(unit);
            setDisponivelReadOnly(unit.hasUhs ?? false);
          }

          setMonth(forecast.mes);
          setAptsOcupados(forecast.aptOcc);
          setDiariaMedia(parseFloat(forecast.ADR.toString().replace('R$', '').trim()));
          setDisponivel(forecast.disponivel);

          const taxa = (forecast.aptOcc / forecast.disponivel) * 100;
          setTaxaOcupacao(parseFloat(taxa.toFixed(2)));

          const receita = forecast.aptOcc * parseFloat(forecast.ADR.toString().replace('R$', '').trim());
          setReceitaHospedagem(receita);

          const revparValue = receita / forecast.disponivel;
          setRevPar(parseFloat(revparValue.toFixed(2)));
        } catch (error) {
          console.error('Error loading existing forecast:', error);
        }
      } else {
        setIsEditing(false);
        setAptsOcupados(0);
        setDiariaMedia(0);
        setTaxaOcupacao(0);
        setRevPar(0);
        setReceitaHospedagem(0);
        setMonth(new Date().getMonth() + 1);
        setDisponivel(0);
        setDisponivelReadOnly(true);
      }
    };

    if (open) {
      loadExistingForecast();
    }
  }, [existingForecastId, open, unitOptions, cookies]);

  useEffect(() => {
    if (open && !isEditing && unitOptions.length > 0) {
      setSelectedUnit(null);
      setDisponivelReadOnly(true);
    }
  }, [open, isEditing, unitOptions]);

  const handleUnitChange = (_event: React.SyntheticEvent, newValue: UnitOption | null) => {
    setSelectedUnit(newValue);
    if (newValue) {
      const selectedOption = unitOptions.find((option) => option.value === newValue.value);
      if (selectedOption) {
        if (selectedOption.hasUhs && selectedOption.disponivel) {
          setDisponivel(selectedOption.disponivel);
          setDisponivelReadOnly(true);
        } else {
          setDisponivel(0);
          setDisponivelReadOnly(false);
        }
        calculateTaxaOcupacao(aptsOcupados, selectedOption.disponivel || 0);
        calculateReceita(aptsOcupados, diariaMedia);
      }
    }
  };

  const calculateTaxaOcupacao = (apts: number, disp: number) => {
    if (disp > 0) {
      const taxa = (apts / disp) * 100;
      setTaxaOcupacao(parseFloat(taxa.toFixed(2)));
    } else {
      setTaxaOcupacao(0);
    }
  };

  const calculateRevPar = (receita: number, disp: number) => {
    if (disp > 0) {
      const revparValue = receita / disp;
      setRevPar(parseFloat(revparValue.toFixed(2)));
    } else {
      setRevPar(0);
    }
  };

  const calculateReceita = (apts: number, diaria: number) => {
    const receita = apts * diaria;
    setReceitaHospedagem(receita);
    calculateRevPar(receita, disponivel);
  };

  const handleAptsOcupadosChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setAptsOcupados(value);
    calculateTaxaOcupacao(value, disponivel);
    calculateReceita(value, diariaMedia);
  };

  const handleDiariaMediaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setDiariaMedia(value);
    calculateReceita(aptsOcupados, value);
  };

  const handleDispoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setDisponivel(value);
    calculateTaxaOcupacao(aptsOcupados, value);
    calculateRevPar(receitaHospedagem, value);
  };

  const handleSave = () => {
    if (!selectedUnit) return;

    const forecastData: ForecastData = {
      hotelId: selectedUnit.value,
      month,
      year,
      aptsOcupados,
      diariaMedia,
      taxaOcupacao,
      revPar,
      receitaHospedagem,
      disp: disponivel,
    };

    onSave(forecastData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEditing ? 'Editar Forecast' : 'Lançamento de Forecast'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              value={selectedUnit}
              onChange={handleUnitChange}
              options={unitOptions}
              renderInput={(params) => <TextField {...params} label="Unidade" placeholder="Selecione uma unidade" fullWidth required />}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              disabled={isEditing}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Ano" type="number" fullWidth value={year} onChange={(e) => setYear(Number(e.target.value))} />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="month-select-label">Mês de Referência</InputLabel>
              <Select labelId="month-select-label" value={month} label="Mês de Referência" onChange={(e) => setMonth(Number(e.target.value))} disabled={isEditing}>
                {meses.map((mes) => (
                  <MenuItem key={mes.value} value={mes.value}>
                    {mes.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
              Dados do Forecast
            </Typography>
          </Grid>

          {selectedUnit && !disponivelReadOnly && (
            <Grid item xs={12}>
              <Alert severity="info">A unidade selecionada não possui valor de UHs cadastrado. Por favor, informe o valor de disponibilidade para o mês.</Alert>
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <TextField label="Disponibilidade" type="number" fullWidth value={disponivel} onChange={handleDispoChange} InputProps={{ inputProps: { min: 0 } }} required disabled={disponivelReadOnly} />
            <FormHelperText>{disponivelReadOnly ? 'Valor automático baseado na unidade selecionada' : 'Informe o valor de disponibilidade para este mês'}</FormHelperText>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Apartamentos Ocupados" type="number" fullWidth value={aptsOcupados} onChange={handleAptsOcupadosChange} InputProps={{ inputProps: { min: 0 } }} required />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Diária Média (R$)" type="number" fullWidth value={diariaMedia} onChange={handleDiariaMediaChange} InputProps={{ inputProps: { min: 0, step: 0.01 } }} required />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: '#f8f9fa',
                padding: 2,
                borderRadius: 1,
                mt: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                Indicadores Calculados
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Taxa de Ocupação
                  </Typography>
                  <Typography variant="h6">{formatarValor(taxaOcupacao, 'porcentagemFormat')}</Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    RevPAR
                  </Typography>
                  <Typography variant="h6">{formatarValor(revPar, 'moeda')}</Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Receita de Hospedagem
                  </Typography>
                  <Typography variant="h6">{formatarValor(receitaHospedagem, 'moeda')}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={!selectedUnit || aptsOcupados <= 0 || diariaMedia <= 0 || disponivel <= 0}>
          {isEditing ? 'Atualizar Forecast' : 'Salvar Forecast'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForecastModal;
